import { useMemo } from "react";
import { map, find, filter, isFunction, groupBy } from "lodash";

import { useTranslationContext } from "~/context/TranslationContext";

const swapPosts = (enPosts, trPosts, activeCode) => {
    if (activeCode === "en") {
        return enPosts;
    }

    return map(enPosts, (post) => {
        const translations = post.translations;

        const translation = find(translations, {
            languageCode: activeCode,
        });

        const fullTranslation = find(trPosts, {
            id: translation?.id,
        });

        if (fullTranslation) {
            return fullTranslation;
        }

        return post;
    });
};

const useGroupByLanguage = (allPosts) => {
    return useMemo(() => {
        return groupBy(allPosts, (post) => {
            const languageCode = post.languageCode || "en";
            return languageCode;
        });
    }, [allPosts]);
};

const useEnglishFallback = (groupedByLang) => {
    const { activeCode } = useTranslationContext();

    return useMemo(() => {
        const enPosts = groupedByLang["en"] || [];
        const trPosts = groupedByLang[activeCode] || enPosts;

        return swapPosts(enPosts, trPosts, activeCode);
    }, [activeCode, groupedByLang]);
};

export const useExcludeFromLanguage = (allPosts) => {
    const { activeCode } = useTranslationContext();

    return useMemo(() => {
        return filter(allPosts, (post) => {
            const languageOptions = post?.languageOptions;
            const languageCodes = languageOptions?.excludeFromLanguage || [];
            return !languageCodes.includes(activeCode);
        });
    }, [allPosts, activeCode]);
};

const useAllDataHelper = (allPosts, initialFilter = null) => {
    const filteredPosts = useMemo(() => {
        if (isFunction(initialFilter)) {
            return filter(allPosts, initialFilter);
        }

        return allPosts || [];
    }, [initialFilter, allPosts]);

    const groupedByLang = useGroupByLanguage(filteredPosts);

    const posts = useEnglishFallback(groupedByLang);

    return useExcludeFromLanguage(posts);
};

export default useAllDataHelper;
